import { inject } from "vue";
import { useToast } from "vue-toastification";
import { useDisplay } from "vuetify";
import {
	buildFileTooLargeForUploadMessage,
	exceedsMultipartPartLimit,
	formatFileSize,
	INITIATE_CONCURRENCY,
	processWithConcurrency
} from "@uploads";
import { detectPlatform, getLargeFileWarningBytes, findLargeFiles } from "@common-ui/utilities/browserDetection";
import { calculateTimeout, uploadSupportEmail } from "@/helpers/utilities";
import { correctDeclaredMimeType, getFilesByType, inferMimeType, normalizeMimeType } from "@/helpers/fileUploads";
import { validateUploadFile, UNREADABLE_FILE_MESSAGE } from "@/helpers/fileValidation";
import { fileUploadDialogKey } from "@/types/keys";
import FileUploadErrorToast from "@/components/FileUploadErrorToast.vue";

import type { UploadFileValidation } from "@/helpers/fileValidation";
import type { Conversation } from "@/types/Conversation";

export type ConversationData = Pick<Conversation, "id" | "name">;
type ConversationDataParam = ConversationData | (() => ConversationData | Promise<ConversationData>);
export interface ProcessFilesOptions {
	onFileUploaded?: (fileType: "image" | "document" | "video") => void;
}

/**
 * LSP-19132: forward the magic-byte-DETECTED MIME type when it disagrees with the browser-DECLARED
 * one, instead of trusting `file.type`.
 *
 * The rule itself lives in helpers/fileUploads `correctDeclaredMimeType` — deliberately ONE
 * implementation. It used to exist as two copies, here and in ActionUploadPhoto.vue, and they drifted:
 * four separate review rounds each found a normalization defect in one copy that the other did not
 * have (raw detected values written verbatim, malformed values trusted, a per-part length bound
 * missing, and agreement on normalized values leaving a parameterized raw type in place). This
 * wrapper adds only this flow's diagnostics.
 *
 * Logging every outcome except the no-op matters: a mislabeled file that slips through does so
 * silently, and a future "why did this one strand" triage should not have to infer which branch ran.
 */
function applyDetectedMimeType(file: File, detectedMimeType: string | null): File {
	const outcome = correctDeclaredMimeType(file, detectedMimeType);
	if (outcome.action === "no-usable-signal") {
		console.info(`[Upload] processFiles — no usable detected MIME type for "${file.name}"; keeping declared type "${file.type}"`, {
			fileName: file.name,
			declaredType: outcome.declaredType,
			detectedMimeType
		});
	} else if (outcome.action !== "unchanged") {
		console.info(`[Upload] processFiles — ${outcome.action} declared MIME type for "${file.name}"`, {
			fileName: file.name,
			declaredType: outcome.declaredType,
			detectedMimeType,
			appliedType: outcome.appliedType
		});
	}
	return outcome.file;
}

/**
 * LSP-19135: MIME values that carry no information about a file's content. `""` is what every major
 * browser reports for a file with no extension (and `inferMimeType` has no extension-table entry to
 * fall back on, so it returns `""` too). `application/octet-stream` is RFC 2046 §4.5.1's "arbitrary
 * binary data" default — a statement that the platform does NOT know the type rather than a claim
 * about it, and what some platforms report where others report `""` for the very same file.
 *
 * Neither value can ever be bucketed by `getFilesByType`, so treating them as "no signal" cannot widen
 * what the partitioner accepts.
 */
const UNKNOWN_DECLARED_MIME_TYPES = new Set(["", "application/octet-stream"]);

function hasNoDeclaredType(file: File): boolean {
	return UNKNOWN_DECLARED_MIME_TYPES.has(normalizeMimeType(inferMimeType(file)));
}

export function useFileUploadFlow() {
	// LSP-16659: an explicit null default. App.vue provides the dialog; EntityDetailPage now calls this
	// flow at setup for the media-grid drop target, and a page mounted outside App (its specs) would
	// otherwise log Vue's "injection not found" warning. Every use below is already optional-chained.
	const fileUploadDialog = inject(fileUploadDialogKey, null);
	const toast = useToast();
	const { mobile } = useDisplay();
	// break-it: admin surface — a rapid double-submit (double-click before the caller's own "saving"
	// state disables its trigger) with the SAME File object(s) previously ran the entire pipeline
	// twice, persisting and reserving an S3 multipart upload for the same bytes twice. Coalesce a
	// call whose every file is already being processed by a still-in-flight call onto that call's
	// result, instead of starting a second, duplicate run.
	const inFlightByFile = new Map<File, Promise<unknown>>();
	const prepareUploadStoragePersistence = (conversationId?: string) => {
		void fileUploadDialog?.value?.requestUploadStoragePersistence(conversationId);
	};

	const processFiles = async (files: File[], conversation: ConversationDataParam, options: ProcessFilesOptions = {}) => {
		const { onFileUploaded } = options;
		if (files.length === 0) {
			return;
		}

		const existingRun = files.map((file) => inFlightByFile.get(file));
		if (existingRun.every((run) => run !== undefined && run === existingRun[0])) {
			console.warn(
				`[Upload] processFiles — coalescing duplicate submission of ${files.length} already-in-flight file(s): ${files
					.map((f) => f.name)
					.join(", ")}`
			);
			return existingRun[0];
		}
		// break-it: admin surface — a PARTIAL overlap (e.g. [A,B] in flight, then [A,C] submitted) must
		// not silently re-run the whole new batch including the already-in-flight file A: that would
		// persist/reserve A's bytes a second time. Drop only the files already covered by an in-flight
		// run and process the genuinely new siblings on their own — the caller only submitted C as new.
		const newFiles = files.filter((file) => !inFlightByFile.has(file));
		if (newFiles.length < files.length) {
			const alreadyInFlight = files.filter((file) => inFlightByFile.has(file));
			console.warn(
				`[Upload] processFiles — dropping ${alreadyInFlight.length} already-in-flight file(s) from this submission, processing only the new sibling(s): ${
					newFiles.map((f) => f.name).join(", ") || "<none>"
				}`
			);
		}
		if (newFiles.length === 0) {
			return;
		}
		files = newFiles;

		const run = (async () => {
			console.log(
				`[Upload] processFiles — ${files.length} file(s): ${files.map((file) => `"${file.name}" (${formatFileSize(file.size)})`).join(", ")}`
			);

			if (mobile.value) {
				window.scrollTo({
					top: 0,
					behavior: "smooth"
				});
			}

			// Validate files fully before resolving conversation — resolving may call api.createConversation()
			// and we must not create orphan DB records for uploads that will never proceed.
			// Step 1: MIME category screen (fast, synchronous).
			const firstPass = getFilesByType(files);
			const firstPassBucketed = new Set([...firstPass.images, ...firstPass.documents, ...firstPass.videos]);

			// LSP-19135: step 1 buckets on the browser-DECLARED type (via `inferMimeType`), but step 2's
			// magic-byte read is the only honest signal about content — and a file step 1 could not bucket
			// never reached step 2, so its bytes were never looked at at all. The user got "unsupported
			// file type" for a perfectly good photo. The realistic trigger is a file with no extension:
			// every major browser reports `type: ""`, `inferMimeType` has no extension to fall back on, and
			// "" matches no bucket. The OS picker's `accept` filter hides such files on macOS, but a
			// drag-and-drop onto the Documents tab bypasses `accept` entirely and Windows pickers offer
			// "All Files", so a real customer can hand us one.
			//
			// Give exactly those files — the ones that would otherwise be refused outright, so this is
			// bounded and adds no read to any file already accepted — the magic-byte read early, stamp the
			// DETECTED type onto the file, and hand the result back to the SAME partitioner below.
			//
			// This is what keeps the partitioner and the validator on ONE rule, which is the constraint
			// `fileValidation.ts` → `allowFromOsMimeFallback` documents at length: that helper deliberately
			// does NOT blanket-allow an unknown inferred MIME, because `getFilesByType`/`ensureFileType`
			// also key off `inferMimeType` and would then silently DROP the file instead of visibly
			// rejecting it — strictly worse than the bug being fixed. Nothing here loosens the validator:
			// `fileValidation.ts` is untouched, and admission is still decided by `getFilesByType` alone.
			// All we change is the quality of the answer `inferMimeType` has to give it. A file whose read
			// is inconclusive, whose bytes are unrecognised, or whose detected type is not an uploadable
			// category still has nothing to be re-typed to, and is still refused out loud below.
			const uncategorised = files.filter((file) => !firstPassBucketed.has(file) && hasNoDeclaredType(file));
			const rescuedByOriginal = new Map<File, File>();
			const rescuedValidations = new Map<File, UploadFileValidation>();
			if (uncategorised.length > 0) {
				console.warn(
					`[Upload] processFiles — ${uncategorised.length} file(s) carry no usable declared MIME type; reading magic bytes to categorise them: ${uncategorised
						.map((file) => `"${file.name}"`)
						.join(", ")}`
				);
				await processWithConcurrency(uncategorised, INITIATE_CONCURRENCY, async (file) => {
					let validation: UploadFileValidation;
					try {
						validation = await validateUploadFile(file);
					} catch (error) {
						console.warn(
							`[Upload] processFiles — magic-byte read threw for uncategorisable "${file.name}"; leaving it rejected`,
							error
						);
						return;
					}
					if (validation.unreadable) {
						rescuedValidations.set(file, validation);
						return;
					}
					const outcome = correctDeclaredMimeType(file, validation.detectedMimeType);
					if (outcome.action !== "corrected") {
						// "no-usable-signal": the read was inconclusive or the bytes were unrecognised, so there
						// is nothing honest to re-type the file TO. Any other non-corrected outcome means the
						// detected type is the same unknown value the partitioner already refused. Either way
						// re-partitioning would bucket this file exactly as the first pass didn't.
						console.warn(`[Upload] processFiles — no detected MIME type to categorise "${file.name}" by; leaving it rejected`, {
							fileName: file.name,
							declaredType: file.type,
							detectedMimeType: validation.detectedMimeType,
							outcome: outcome.action
						});
						return;
					}
					console.info(`[Upload] processFiles — categorised "${file.name}" by its magic bytes as ${outcome.appliedType}`, {
						fileName: file.name,
						declaredType: outcome.declaredType,
						detectedMimeType: validation.detectedMimeType,
						appliedType: outcome.appliedType
					});
					rescuedByOriginal.set(file, outcome.file);
					rescuedValidations.set(outcome.file, validation);
				});
			}

			// Re-run the SAME partitioner with each rescued file replaced by its re-typed copy. The re-typed
			// copy — not the original — is what goes forward: `getFilesByType`, `inferFileCategory` and the
			// S3 key extension are all derived from the declared type downstream, so forwarding the original
			// would re-open this same hole one layer later.
			const candidates = files.map((file) => rescuedByOriginal.get(file) ?? file);
			const { images, documents, videos } = rescuedByOriginal.size > 0 ? getFilesByType(candidates) : firstPass;
			const supportedFiles = [...images, ...documents, ...videos];
			for (const file of candidates) {
				if (!supportedFiles.includes(file)) {
					if (rescuedValidations.get(file)?.unreadable) {
						const message = `"${file.name}": ${UNREADABLE_FILE_MESSAGE}`;
						toast.error(message, { timeout: calculateTimeout(message) });
						continue;
					}
					// The rejection is visible to the user, but not to a triage six weeks later — log which
					// signal the partitioner actually saw, and whether a magic-byte read had already been
					// tried on this file and still came up with nothing uploadable.
					console.warn(`[Upload] processFiles — "${file.name}" is not an uploadable file type; rejecting`, {
						fileName: file.name,
						declaredType: file.type,
						inferredMimeType: inferMimeType(file),
						magicBytesRead: rescuedValidations.has(file) || uncategorised.includes(file)
					});
					toast.error({ component: FileUploadErrorToast, props: { fileName: file.name } }, { timeout: 10000 });
				}
			}
			if (supportedFiles.length === 0) {
				return;
			}

			// Step 2: Magic-bytes validation (async). Cloud files time out after 200ms and fall back to
			// file.type — the same signal getFilesByType already checked — so the extra wait is minimal.
			// LSP-16027: must wrap validateUploadFile in an arrow — passing it directly to .map leaks the
			// (index, array) callback args into the helper's `allow` parameter (added in LSP-15806). That
			// makes `matchesAllowed(mime, /* number */ 0)` call `(0).some(...)`, which throws
			// "TypeError: t.some is not a function", gets silently swallowed by the helper's try/catch,
			// and drops every legitimate upload.
			// LSP-19132: validateUploadFile rather than isValidUploadFile — same magic-byte read, but it
			// also reports the type it detected, which correctDeclaredMimeType needs below. Taking it from
			// this one read (rather than re-sniffing) means the correction can never disagree with the
			// read that approved the file, and costs no extra I/O.
			const validationResults: UploadFileValidation[] = supportedFiles.map(() => ({ valid: false, detectedMimeType: null }));
			// Keep validation bounded and isolate one validator/read failure to its file. The index is
			// retained so filtering cannot pair a later target with the wrong input file.
			await processWithConcurrency(supportedFiles, INITIATE_CONCURRENCY, async (file, index) => {
				// LSP-19135: a rescued file's bytes were already read above, to work out what category it
				// belongs in. Reuse that verdict rather than reading the same bytes a second time — beyond
				// the wasted I/O, the read that categorised the file and the read that approves it must be
				// the same read, or the two could disagree about the very file they both examined.
				const alreadyValidated = rescuedValidations.get(file);
				if (alreadyValidated) {
					validationResults[index] = alreadyValidated;
					return;
				}
				try {
					validationResults[index] = await validateUploadFile(file);
				} catch (error) {
					console.warn(`[Upload] Validation failed for "${file.name}"; rejecting only this file`, error);
				}
			});
			// Accept by INDEX, never by instance identity. correctDeclaredMimeType returns a NEW File for a
			// mismatched type, so the previous `!validFiles.includes(file)` membership test would have read
			// every corrected file as rejected and fired a spurious "unable to upload" toast for exactly the
			// files this change is meant to rescue.
			const validFiles: File[] = [];
			for (const [index, file] of supportedFiles.entries()) {
				const { valid, detectedMimeType, unreadable } = validationResults[index];
				if (unreadable) {
					const message = `"${file.name}": ${UNREADABLE_FILE_MESSAGE}`;
					toast.error(message, { timeout: calculateTimeout(message) });
					continue;
				}
				if (!valid) {
					// LSP-23172 (silent-failure rule): a crowd tester's IMG_0342.mov was turned away here and left no
					// trace, so nobody could tell whether its bytes were unrecognised or read as a disallowed type.
					console.warn(`[Upload] processFiles — "${file.name}" failed the magic-byte check; rejecting`, {
						fileName: file.name,
						fileSize: file.size,
						declaredType: file.type,
						inferredMimeType: inferMimeType(file),
						detectedMimeType
					});
					toast.error({ component: FileUploadErrorToast, props: { fileName: file.name } }, { timeout: 10000 });
					continue;
				}
				validFiles.push(applyDetectedMimeType(file, detectedMimeType));
			}
			if (validFiles.length === 0) {
				return;
			}

			// Step 3: upload-size screen. LSP-19397 — a file needing more parts than the multipart engine
			// can describe is refused by the API, and until this screen existed that refusal arrived as the
			// wire error `totalChunks must be an integer between 1 and 1000.` AFTER the conversation had
			// been created, leaving an orphan behind and telling the customer nothing about the real limit.
			// Screened here, above the conversation resolution below, for the same reason the large-file
			// warning is: this step's whole contract is that nothing is created for an upload that cannot
			// proceed. `exceedsMultipartPartLimit` is the same rule `initiateUploadTargets` refuses on, so
			// this screen can never reject a file the engine would have accepted, or accept one it rejects.
			const uploadableFiles = validFiles.filter((file) => {
				if (!exceedsMultipartPartLimit(file.size)) return true;
				console.warn(
					`[Upload] "${file.name}" (${formatFileSize(file.size)}) needs more multipart parts than the engine can describe; refusing before the conversation is resolved`
				);
				// WARNING, not error: the customer did nothing wrong by picking a large video, and admin
				// shows red only for genuine faults (Jake, LSP-19397 review).
				const message = buildFileTooLargeForUploadMessage(file.name, file.size, uploadSupportEmail());
				toast.warning(message, { timeout: calculateTimeout(message) });
				return false;
			});
			if (uploadableFiles.length === 0) {
				return;
			}

			const uploadDialog = fileUploadDialog?.value;
			if (typeof uploadDialog?.openDialog !== "function") {
				const message = "We couldn't start that upload. Please refresh and try again.";
				console.error("[Upload] processFiles — upload dialog unavailable before conversation creation", { fileCount: uploadableFiles.length });
				toast.error(message, { timeout: calculateTimeout(message) });
				return { success: false, counts: null };
			}

			// Warn before resolving the conversation — a user-cancelled upload must not
			// leave an orphan conversation behind.
			const platform = detectPlatform();
			if (platform !== "desktop") {
				const largeFiles = findLargeFiles(uploadableFiles, getLargeFileWarningBytes(platform));
				if (largeFiles.length > 0) {
					const confirmed = (await uploadDialog.confirmLargeFileWarning(largeFiles, platform)) ?? false;
					if (!confirmed) {
						return { success: false, cancelled: true, counts: null };
					}
				}
			}

			const { id, name } = typeof conversation === "function" ? await conversation() : conversation;
			const result = await uploadDialog.openDialog(uploadableFiles, id, name, onFileUploaded);
			if (!result?.success && !result?.cancelled && !result?.deferred && !result?.checking) {
				const message = `Sorry, we were unable to upload some files. Please try to upload them again.`;
				toast.error(message, { timeout: calculateTimeout(message) });
			}

			return result;
		})();

		for (const file of files) inFlightByFile.set(file, run);
		try {
			return await run;
		} finally {
			for (const file of files) {
				if (inFlightByFile.get(file) === run) inFlightByFile.delete(file);
			}
		}
	};

	return {
		processFiles,
		prepareUploadStoragePersistence
	};
}
